import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import { fetchProfileFeed } from '@actions/educator';
import { useDispatch, useSelector } from 'react-redux';
import useProfile from '@api/hooks/profile/useProfile';
import useProfileFeed from '@api/hooks/profile/useProfileFeed';
import { Spacings, mediaQuery } from '@styles/index';
import { getTotalActiveBatches } from '@selectors/auth';
import { BatchesEmptyState } from '@comp/EmptyState/BatchGroupEmptyState';
import usePlatformInfo from '@hooks/usePlatformInfo';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import Dynamic from '@base/Dynamic/Dynamic';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import {
  getIsSubscribedToOffline,
  getIsSubscribedToPlus
} from '@selectors/subscription';
import useEducatorCuratedPlaylist from '@cont/Content/FreeLesson/useEducatorCuratedPlaylist';
import CuratedPlayList from '@cont/FeedTypeBlock/CuratedPlayList';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { EDUCATOR_SIGNUP_BANNER } from '@comp/SignupGenericBanner/constant';
import { BANNER_CLICKED, EDUCATOR_PROFILE_VIEWED } from './constants';
import {
  addEducatorInLocalStorage,
  getShowEducatorLibraryIntroBanner,
  isEducatorLibraryEnabled
} from './utils';
import EducatorLibraryIntroBanner from './EducatorLibraryIntroBanner';
import SingleFeedBlock from './SingleFeedBlock';
import Feedrow from './Feedrow';

const DppFinishSummary = Dynamic(() =>
  import('@cont/Practice/Listing/DppFinishSummary')
);

const SignupGenericBanner = Dynamic(() =>
  import('@comp/SignupGenericBanner/SignupGenericBanner')
);

const ListContainer = styled.div`
  grid-column: ${({ $isExEducator }) => ($isExEducator ? '1/21' : '1/19')};
  grid-column-start: ${({ $isExEducator }) => ($isExEducator ? 3 : 1)};
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    margin: ${Spacings.SPACING_2B} 0 0;
  }
`;

const StyledSignupBanner = styled(SignupGenericBanner)`
  margin-top: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  }
`;

const HEADER_HEIGHT = 120;
const MORE_COURSES_MARGIN_TOP = 80;
const BANNER_HEIGHT = 120;

const EducatorProfileFeed = ({
  username,
  isPlatinumEducator,
  isEducatorPage = null
}) => {
  const { profileFeed, mutate: refetchProfileFeed } = useProfileFeed(username);

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const selectedGoalUID = useSelectedGoalUID();
  const isSubscribedToGoal = useSelector(
    getIsSubscribedToPlus(selectedGoalUID)
  );
  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(selectedGoalUID)
  );

  const routeToPage = isPlatinumEducator ? 'batch-group' : 'courses';
  const { profileData = {} } = useProfile(username) || { profileData: {} };
  const { isEducator, isExEducator, uid } = profileData;
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });
  const { showSummary } = useFinishSummary();

  const { curatedPlaylistData } = useEducatorCuratedPlaylist(uid);

  const dispatch = useDispatch();
  const topicsSectionRef = useRef();

  const { businessPlatform } = usePlatformInfo();
  const activeBatchSubscriptions = useSelector(getTotalActiveBatches);
  const analyticsProps = {
    is_batch_subscription_active: activeBatchSubscriptions > 0,
    business_platform: businessPlatform,
    number_of_active_batch_subscription: activeBatchSubscriptions
  };

  const [showEducatorLibraryIntroBanner, setShowEducatorLibraryIntroBanner] =
    useState(getShowEducatorLibraryIntroBanner(profileFeed.results, username));

  useEffect(() => {
    if (isEducatorLibraryEnabled(profileFeed.results)) {
      segment.track(EDUCATOR_PROFILE_VIEWED, {
        ...goalSegmentData,
        educator_username: username,
        last_primary_source: 'Educator Profile',
        last_primary_source_section: 'Educator Profile'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileFeed.results?.length]);

  useEffect(() => {
    setShowEducatorLibraryIntroBanner(
      getShowEducatorLibraryIntroBanner(profileFeed.results, username)
    );
  }, [profileFeed.results, username]);

  useEffect(() => {
    if (isLoggedIn) refetchProfileFeed();
  }, [isLoggedIn, refetchProfileFeed]);

  if (!((isEducator || isExEducator) && profileFeed?.results)) return null;

  // handling no batch case for profile feed for Platform batch group educator
  const isProfileFeedEmptyForPlatformBatch =
    profileFeed.results?.length === 0 && isPlatinumEducator;

  if (isProfileFeedEmptyForPlatformBatch)
    return (
      <ListContainer>
        <BatchesEmptyState />
      </ListContainer>
    );

  const loadMore = () =>
    dispatch(fetchProfileFeed(username, selectedGoalUID, true)); // Todo: Update load more flow

  const bannerAndSingleBlock =
    profileFeed.results?.length === 2 &&
    profileFeed.results[0].type === 'banner';

  const onClickIntroBanner = () => {
    addEducatorInLocalStorage(username);
    setShowEducatorLibraryIntroBanner(false);
    window.scrollTo({
      top:
        topicsSectionRef.current?.offsetTop -
        (HEADER_HEIGHT + MORE_COURSES_MARGIN_TOP + BANNER_HEIGHT),
      behavior: 'smooth'
    });
    segment.track(BANNER_CLICKED, {
      ...goalSegmentData,
      banner_title: 'Educator Library',
      banner_type: 'Generic',
      last_primary_source: 'Educator Profile',
      last_primary_source_section: 'Educator Profile'
    });
  };

  return (
    <ListContainer $isExEducator={isExEducator}>
      {showEducatorLibraryIntroBanner &&
        (isSubscribedToGoal || isOfflineSubscription) && (
          <EducatorLibraryIntroBanner onClickIntroBanner={onClickIntroBanner} />
        )}
      {curatedPlaylistData.hasCuratedPlaylistData && (
        <CuratedPlayList
          block={curatedPlaylistData}
          isEducator
          username={username}
          educatorUID={uid}
        />
      )}
      <StyledSignupBanner
        data={EDUCATOR_SIGNUP_BANNER}
        goalUID={selectedGoalUID}
      />
      {isPlatinumEducator ||
      (profileFeed.results?.length > 1 && !bannerAndSingleBlock) ? (
        <InfiniteList
          data={profileFeed}
          loadMore={loadMore}
          RenderItem={Feedrow}
          gridColumn="span 18"
          extraProps={{
            educatorUID: uid,
            topicsSectionRef,
            username,
            analyticsProps,
            routeToPage,
            isPlatinumEducator
          }}
        />
      ) : (
        <SingleFeedBlock
          isBannerPresent={bannerAndSingleBlock}
          username={username}
          profileFeed={profileFeed}
          isEducatorPage={isEducatorPage}
        />
      )}
      {showSummary && <DppFinishSummary source="Educator Profile" />}
    </ListContainer>
  );
};

EducatorProfileFeed.propTypes = {
  username: PropTypes.string.isRequired
};

export default EducatorProfileFeed;
